import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useIdentityPreferences } from '@nest/common-core-frontend-react';
import { Button, Skeleton, Textbox } from '@nest/uikit-react';
import { ExpandIcon, MoveIcon, XIcon } from 'lucide-react';
import { Children, createContext, isValidElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { ReorderItemsOrderDialog } from './ReorderItemsOrderDialog';
const FormDataGridContext = createContext(null);
export const useFormDataGridContext = () => {
    const context = useContext(FormDataGridContext);
    if (!context) {
        throw new Error('useFormDataGridContext must be used within a FormDataGrid');
    }
    return context;
};
const ResizableColumnHeader = ({ column, width, onResize }) => {
    const [isResizing, setIsResizing] = useState(false);
    const [startX, setStartX] = useState(0);
    const [startWidth, setStartWidth] = useState(0);
    const [currentWidth, setCurrentWidth] = useState(width);
    const thRef = useRef(null);
    // Update current width when prop width changes
    useEffect(() => {
        setCurrentWidth(width);
    }, [width]);
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
    }, [currentWidth]);
    const handleMouseMove = useCallback((e) => {
        if (!isResizing)
            return;
        const diff = e.clientX - startX;
        const newWidth = Math.max(column.minWidth ?? 100, startWidth + diff);
        setCurrentWidth(newWidth); // Update only local state during resize
    }, [isResizing, startX, startWidth, column.minWidth]);
    const handleMouseUp = useCallback(() => {
        if (isResizing) {
            onResize(currentWidth); // Save to preferences only when resize ends
            setIsResizing(false);
        }
    }, [isResizing, currentWidth, onResize]);
    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);
    return (_jsxs("th", { ref: thRef, className: "text-left p-1 font-medium text-sm relative", style: {
            minWidth: column.minWidth ?? 100,
            width: currentWidth
        }, children: [column.header, column.resizable !== false && (_jsx("div", { className: "absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-border/50 active:bg-border", onMouseDown: handleMouseDown }))] }));
};
export const FormDataGrid = ({ control, fieldArrayName, preferenceKey, children, className, onAddItem, onRemoveItem, addButtonText = 'Přidat položku', emptyStateText = 'Žádné položky nebyly přidány', mode = 'auto', height = 400, rowHeight = 44 }) => {
    const { fields } = useFieldArray({
        control,
        name: fieldArrayName
    });
    const [isFullViewport, setIsFullViewport] = useState(false);
    const { getValue, updateValues } = useIdentityPreferences({
        applicationName: 'desktop-frontend-react'
    });
    const [isReorderDialogOpen, setIsReorderDialogOpen] = useState(false);
    const containerRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(0);
    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return undefined;
        }
        setViewportHeight(el.clientHeight);
        const handleScroll = () => {
            setScrollTop(el.scrollTop);
        };
        const handleResize = () => {
            setViewportHeight(el.clientHeight);
        };
        el.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);
        return () => {
            el.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [mode]);
    const [columnFilters, setColumnFilters] = useState({});
    // Extract columns from children
    const columns = useMemo(() => {
        const childrenArray = Children.toArray(children);
        return childrenArray
            .filter(child => {
            if (!isValidElement(child))
                return false;
            // Direct FormDataGridColumn component
            if (child.type === FormDataGridColumn)
                return true;
            // Helper component with static properties
            if (child.type?.isDataGridHelper)
                return true;
            return false;
        })
            .map(child => {
            if (child.type === FormDataGridColumn) {
                return child.props;
            }
            // Helper component - call getColumnProps
            if (child.type?.isDataGridHelper && child.type?.getColumnProps) {
                return child.type.getColumnProps(child.props, fieldArrayName);
            }
            throw new Error('Unknown column type');
        });
    }, [children, fieldArrayName]);
    // Check if any column has filters
    const hasFilters = useMemo(() => {
        return columns.some(col => col.showFilter);
    }, [columns]);
    // Load preferences
    const savedPreferences = getValue(`formDataGrid.${preferenceKey}`);
    const defaultPreferences = {
        columnOrder: columns.map(col => col.name),
        columnSizes: Object.fromEntries(columns.map(col => [col.name, col.defaultWidth ?? 150]))
    };
    const preferences = useMemo(() => ({
        ...defaultPreferences,
        ...savedPreferences
    }), [savedPreferences, defaultPreferences]);
    const updatePreferences = async (updates) => {
        const newPreferences = { ...preferences, ...updates };
        await updateValues({
            [`formDataGrid.${preferenceKey}`]: newPreferences
        });
    };
    // Update filter value (local state only)
    const updateFilter = useCallback((columnKey, filterValue) => {
        setColumnFilters(prev => {
            const newFilters = { ...prev };
            if (filterValue) {
                newFilters[columnKey] = filterValue;
            }
            else {
                delete newFilters[columnKey];
            }
            return newFilters;
        });
    }, []);
    // Reorder columns based on preferences
    const orderedColumns = useMemo(() => {
        const { columnOrder } = preferences;
        const orderedCols = [...columns];
        // Sort by saved order
        orderedCols.sort((a, b) => {
            const aIndex = columnOrder.indexOf(a.name);
            const bIndex = columnOrder.indexOf(b.name);
            if (aIndex === -1 && bIndex === -1)
                return 0;
            if (aIndex === -1)
                return 1;
            if (bIndex === -1)
                return -1;
            return aIndex - bIndex;
        });
        return orderedCols;
    }, [columns, preferences]);
    // Filter function with index mapping
    const getFilteredFieldsWithIndices = useMemo(() => {
        if (!hasFilters || Object.keys(columnFilters).length === 0) {
            return fields.map((field, index) => ({ field, originalIndex: index }));
        }
        return fields
            .map((field, index) => ({ field, originalIndex: index }))
            .filter(({ originalIndex }) => {
            const formData = control._formValues;
            const itemData = formData[fieldArrayName]?.[originalIndex];
            return orderedColumns.every(column => {
                if (!column.showFilter || !column.name)
                    return true;
                const filterValue = columnFilters[column.name];
                if (!filterValue)
                    return true;
                // Get the value from the field path
                const fieldValue = column.name.split('.').reduce((obj, key) => obj?.[key], itemData);
                const stringValue = String(fieldValue || '').toLowerCase();
                const filterLower = filterValue.toLowerCase();
                return stringValue.includes(filterLower);
            });
        });
    }, [fields, columnFilters, hasFilters, orderedColumns, control._formValues, fieldArrayName]);
    const contextValue = useMemo(() => ({
        control,
        fieldArrayName,
        columns: orderedColumns,
        preferences,
        updatePreferences
    }), [control, fieldArrayName, orderedColumns, preferences, updatePreferences]);
    const handleToggleFullViewport = () => {
        setIsFullViewport(!isFullViewport);
    };
    const handleReorderColumns = () => {
        setIsReorderDialogOpen(true);
    };
    const handleReorderConfirm = (newOrder) => {
        updatePreferences({ columnOrder: newOrder });
        setIsReorderDialogOpen(false);
    };
    const containerClass = isFullViewport ? 'fixed inset-0 z-50 bg-background' : `relative border border-border rounded-lg ${className}`;
    const isFixedHeightMode = mode === 'fixed';
    const totalRows = getFilteredFieldsWithIndices.length;
    const effectiveViewportHeight = isFixedHeightMode ? (viewportHeight > 0 ? viewportHeight : height) : 0;
    const overscanCount = 6;
    const startIndex = isFixedHeightMode ? Math.max(0, Math.floor(scrollTop / rowHeight) - overscanCount) : 0;
    const visibleCount = isFixedHeightMode ? Math.ceil(effectiveViewportHeight / rowHeight) + overscanCount * 2 : totalRows;
    const endIndex = isFixedHeightMode ? Math.min(totalRows, startIndex + visibleCount) : totalRows;
    const topPadding = isFixedHeightMode ? startIndex * rowHeight : 0;
    const bottomPadding = isFixedHeightMode ? Math.max(0, (totalRows - endIndex) * rowHeight) : 0;
    // Skeleton rows to fill viewport when there are fewer real rows than viewport capacity
    const rowsPerViewport = isFixedHeightMode ? Math.max(1, Math.ceil(effectiveViewportHeight / rowHeight)) : 0;
    const viewportStartIndex = isFixedHeightMode ? Math.max(0, Math.floor(scrollTop / rowHeight)) : 0;
    const viewportEndIndex = isFixedHeightMode ? Math.min(totalRows, viewportStartIndex + rowsPerViewport) : totalRows;
    const visibleInViewportCount = isFixedHeightMode ? Math.max(0, viewportEndIndex - viewportStartIndex) : totalRows;
    const skeletonRowCount = isFixedHeightMode ? Math.max(0, rowsPerViewport - visibleInViewportCount) : 0;
    const adjustedBottomPadding = isFixedHeightMode ? Math.max(0, bottomPadding - skeletonRowCount * rowHeight) : bottomPadding;
    return (_jsx(FormDataGridContext.Provider, { value: contextValue, children: _jsxs("div", { className: containerClass, children: [_jsx("div", { className: "flex justify-between items-center p-4 border-b border-border", children: _jsxs("div", { className: "flex gap-2", children: [_jsxs(Button, { type: "button", variant: "outline", size: "sm", onClick: handleReorderColumns, className: "flex items-center gap-2", children: [_jsx(MoveIcon, { size: 16 }), "Zm\u011Bnit po\u0159ad\u00ED"] }), _jsxs(Button, { type: "button", variant: "outline", size: "sm", onClick: handleToggleFullViewport, className: "flex items-center gap-2", children: [_jsx(ExpandIcon, { size: 16 }), isFullViewport ? 'Zmenšit' : 'Celá obrazovka'] })] }) }), _jsx("div", { ref: containerRef, className: isFixedHeightMode ? 'overflow-auto' : 'overflow-x-auto', style: isFixedHeightMode ? { height } : undefined, children: _jsxs("table", { className: "w-full", children: [_jsxs("thead", { className: isFixedHeightMode ? 'bg-muted border-b border-border sticky top-0 z-20' : 'bg-muted/50 border-b border-border', children: [_jsxs("tr", { children: [orderedColumns.map(column => (_jsx(ResizableColumnHeader, { column: column, width: preferences.columnSizes[column.name] ?? column.defaultWidth ?? 150, onResize: newWidth => {
                                                    updatePreferences({
                                                        columnSizes: {
                                                            ...preferences.columnSizes,
                                                            [column.name]: newWidth
                                                        }
                                                    });
                                                } }, column.name))), onRemoveItem && _jsx("th", { className: "text-left p-1 font-medium text-sm min-w-[60px] sticky right-0 bg-background", children: "Akce" })] }), hasFilters && (_jsxs("tr", { className: "bg-muted border-b border-border", children: [orderedColumns.map(column => (_jsx("th", { className: "p-2", style: {
                                                    minWidth: column.minWidth ?? 100,
                                                    width: preferences.columnSizes[column.name] ?? column.defaultWidth ?? 150
                                                }, children: column.showFilter && (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx(Textbox, { value: columnFilters[column.name] ?? '', onChange: e => updateFilter(column.name, e.target.value), placeholder: `Filtrovat ${column.header.toLowerCase()}...`, className: "w-full text-xs" }), columnFilters[column.name] && (_jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => updateFilter(column.name, ''), className: "p-1 h-6 w-6 hover:bg-red-100", children: _jsx(XIcon, { className: "h-3 w-3" }) }))] })) }, `filter-${column.name}`))), onRemoveItem && _jsx("th", { className: "p-2 min-w-[60px] sticky right-0 bg-background" })] }))] }), _jsxs("tbody", { children: [isFixedHeightMode && totalRows > 0 && (_jsx("tr", { "aria-hidden": "true", children: _jsx("td", { colSpan: orderedColumns.length + (onRemoveItem ? 1 : 0), style: { height: topPadding } }) })), getFilteredFieldsWithIndices.slice(startIndex, endIndex).map(({ field, originalIndex }) => (_jsxs("tr", { className: "border-b border-border hover:bg-muted", children: [orderedColumns.map(column => (_jsx("td", { className: "p-1", style: {
                                                    minWidth: column.minWidth ?? 100,
                                                    width: preferences.columnSizes[column.name] ?? column.defaultWidth ?? 150
                                                }, children: column.render ? (column.render({
                                                    control,
                                                    index: originalIndex,
                                                    field,
                                                    value: undefined,
                                                    formValues: control._formValues
                                                })) : (_jsx("div", { children: "-" })) }, column.name))), onRemoveItem && (_jsx("td", { className: "p-1 sticky right-0 bg-background", children: _jsx(Button, { type: "button", variant: "outline", size: "sm", onClick: () => onRemoveItem(originalIndex), className: "text-red-600 hover:text-red-700 hover:bg-red-50", children: "\u00D7" }) }))] }, field.id))), isFixedHeightMode &&
                                        skeletonRowCount > 0 &&
                                        Array.from({ length: skeletonRowCount }).map((_, i) => (_jsxs("tr", { className: "border-b border-border", children: [orderedColumns.map(column => (_jsx("td", { className: "p-1", style: {
                                                        minWidth: column.minWidth ?? 100,
                                                        width: preferences.columnSizes[column.name] ?? column.defaultWidth ?? 150,
                                                        height: rowHeight
                                                    }, children: _jsx(Skeleton, { className: "h-4 w-3/4" }) }, `sk-${column.name}-${i}`))), onRemoveItem && (_jsx("td", { className: "p-1 sticky right-0 bg-background", style: { height: rowHeight }, children: _jsx(Skeleton, { className: "h-4 w-6" }) }))] }, `skeleton-${i}`))), isFixedHeightMode && totalRows > 0 && (_jsx("tr", { "aria-hidden": "true", children: _jsx("td", { colSpan: orderedColumns.length + (onRemoveItem ? 1 : 0), style: { height: adjustedBottomPadding } }) })), getFilteredFieldsWithIndices.length === 0 && fields.length === 0 && (_jsx("tr", { children: _jsx("td", { colSpan: orderedColumns.length + (onRemoveItem ? 1 : 0), className: "p-8 text-center text-muted-foreground", children: _jsxs("div", { children: [_jsx("p", { className: "mb-2", children: emptyStateText }), onAddItem && (_jsx(Button, { type: "button", onClick: onAddItem, children: addButtonText }))] }) }) })), getFilteredFieldsWithIndices.length === 0 && fields.length > 0 && (_jsx("tr", { children: _jsx("td", { colSpan: orderedColumns.length + (onRemoveItem ? 1 : 0), className: "p-8 text-center text-muted-foreground", children: _jsxs("div", { children: [_jsx("p", { className: "mb-2", children: "\u017D\u00E1dn\u00E9 v\u00FDsledky nenalezeny" }), _jsx("p", { className: "text-sm", children: "Zkuste upravit filtry" })] }) }) }))] })] }) }), fields.length > 0 && (_jsx("div", { className: "p-4 border-t border-border bg-muted", children: _jsxs("div", { className: "flex justify-between items-center", children: [_jsx("div", { className: "text-sm text-muted-foreground", children: hasFilters && Object.keys(columnFilters).length > 0
                                    ? `Zobrazeno ${getFilteredFieldsWithIndices.length} z ${fields.length} položek`
                                    : `Celkem položek: ${fields.length}` }), onAddItem && (_jsx(Button, { type: "button", variant: "outline", onClick: onAddItem, children: addButtonText }))] }) })), _jsx(ReorderItemsOrderDialog, { isOpen: isReorderDialogOpen, onClose: () => setIsReorderDialogOpen(false), onConfirm: handleReorderConfirm, items: orderedColumns.map(col => ({ id: col.name, label: col.header })), title: "Zm\u011Bnit po\u0159ad\u00ED sloupc\u016F" })] }) }));
};
// Column component (used for declarative syntax)
export const FormDataGridColumn = (_props) => {
    return null; // This component doesn't render anything, it's just for type checking
};
//# sourceMappingURL=FormDataGrid.js.map